/**
 * Global uppercase policy for data-entry fields.
 *
 * Every text field in the app captures data in CAPITAL LETTERS. The value is
 * uppercased as the user types, so what is shown, what is validated and what is
 * submitted to the API are all the same string.
 *
 * Some values are deliberately left untouched because their casing is
 * significant: FBR bearer tokens, credentials, emails and URLs stop working if
 * their case is changed.
 */

// Field names whose value must keep the exact casing the user entered.
const CASE_SENSITIVE_FIELDS = new Set(
  [
    // FBR API credentials - case-sensitive bearer tokens
    "sandBoxToken",
    "productiontoken",
    "productionToken",
    "token",
    // Credentials
    "password",
    "cPassword",
    "confirmPassword",
    "newPassword",
    "oldPassword",
    "currentPassword",
    "email",
    "userEmail",
    // URLs / connection strings / keys
    "website",
    "url",
    "domain",
    "dbConnection",
    "licenseKey",
  ].map((name) => name.toLowerCase())
);

// Input types that are never uppercased (no letters, or case-sensitive by nature).
const CASE_SENSITIVE_TYPES = new Set([
  "password",
  "email",
  "url",
  "number",
  "tel",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
  "file",
  "checkbox",
  "radio",
  "range",
  "color",
  "hidden",
  "submit",
  "button",
  "reset",
]);

/** Uppercase a value, leaving non-strings (null, numbers) untouched. */
export const toUpperCaseValue = (value) =>
  typeof value === "string" ? value.toUpperCase() : value;

/** Whether a field with this name/type should capture capital letters. */
export const shouldUpperCase = ({ name, type } = {}) => {
  if (type && CASE_SENSITIVE_TYPES.has(String(type).toLowerCase())) return false;
  if (name && CASE_SENSITIVE_FIELDS.has(String(name).toLowerCase())) return false;
  return true;
};

/**
 * Wraps a change handler so the value reaches it already uppercased.
 * Use on raw <input> elements: onChange={withUpperCase(handleChange)}
 */
export const withUpperCase =
  (handler, options = {}) =>
  (event) => {
    if (!handler) return;
    const target = event?.target;
    if (target && typeof target.value === "string") {
      const name = options.name ?? target.name;
      const type = options.type ?? target.type;
      if (shouldUpperCase({ name, type })) {
        target.value = target.value.toUpperCase();
      }
    }
    handler(event);
  };

/**
 * Normalises an input/textarea/select target for a change handler, returning the
 * value already uppercased when the field should capture capital letters.
 *
 *   const { name, value } = upperCaseInput(e.target);
 *
 * <select> is deliberately excluded: its value is an option code (province,
 * registration type, scenario id) that is submitted to FBR verbatim.
 */
export const upperCaseInput = (target) => {
  if (!target) return {};
  const { name, value, type, checked, files } = target;
  const tag = String(target.tagName || "input").toLowerCase();
  const uppercase = tag !== "select" && shouldUpperCase({ name, type });
  return {
    name,
    type,
    checked,
    files,
    value: uppercase ? toUpperCaseValue(value) : value,
  };
};
